
import { useState, useEffect } from 'react';
import { relationshipService } from '@/services/relationshipService';
import { teamService } from '@/services/teamService';
import { FanTeam, Team } from '@/types/database';
import { toast } from 'sonner';
import { useAuth } from './useAuth';
import { useInternalAuth } from './useInternalAuth';

export const useUserTeams = () => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [userTeams, setUserTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);

  const user = supabaseUser || internalUser;

  const loadUserTeams = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      console.log('Loading teams for user:', user.id);
      const teams = await teamService.getUserTeams(user.id);
      console.log('User teams loaded:', teams);
      setUserTeams(teams);
    } catch (error) {
      console.error('Error loading user teams:', error);
      toast.error('Failed to load your teams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserTeams();
  }, [user?.id]);

  return {
    userTeams,
    loading,
    loadUserTeams
  };
};

export const useFanTeams = () => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [fanTeams, setFanTeams] = useState<FanTeam[]>([]);
  const [loading, setLoading] = useState(false);

  const user = supabaseUser || internalUser;

  const loadFanTeams = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const teams = await relationshipService.getFanTeams(user.id);
      setFanTeams(teams);
    } catch (error) {
      console.error('Error loading fan teams:', error);
      toast.error('Failed to load favorite teams');
    } finally {
      setLoading(false);
    }
  };

  const followTeam = async (teamId: number) => {
    if (!user?.id) return;
    
    try {
      const fanTeam = await relationshipService.addFanTeam(user.id, teamId);
      setFanTeams(prev => [...prev, fanTeam]);
      toast.success('Team added to favorites');
    } catch (error) {
      console.error('Error following team:', error);
      toast.error('Failed to follow team');
    }
  };

  const unfollowTeam = async (teamId: number) => {
    if (!user?.id) return;
    
    try {
      await relationshipService.removeFanTeam(user.id, teamId);
      setFanTeams(prev => prev.filter(ft => ft.team_id !== teamId));
      toast.success('Team removed from favorites');
    } catch (error) {
      console.error('Error unfollowing team:', error);
      toast.error('Failed to unfollow team');
    }
  };

  useEffect(() => {
    loadFanTeams();
  }, [user?.id]);

  return {
    fanTeams,
    loading,
    loadFanTeams,
    followTeam,
    unfollowTeam
  };
};
